import { asc, clamp, findClosest, roundValueToStep } from './slider.helpers';

import type { CSSProperties, ReactNode } from 'react';

import type { Mark, SliderProps, SliderValue } from '../../slider/slider.types';
import type { Axis } from '../types';

/** Default snap threshold as a percentage of the slider range when `snapToMarks` is `true`. */
const DEFAULT_SNAP_THRESHOLD_PERCENT = 2;
/** Estimated width of a single character in a mark label, as a percentage of the slider range. */
const LABEL_CHAR_WIDTH_PERCENT = 1.5;
/** Minimum horizontal gap (in percent) required between labels before staggering kicks in. */
const LABEL_GAP_PERCENT = 2.5;
/** Minimum width (in percent) assumed for any mark label, regardless of text length. */
const LABEL_MIN_WIDTH_PERCENT = 4;
/** Maximum number of stagger levels (rows) available for mark labels. */
const LABEL_LEVEL_COUNT = 3;
/** Tolerance (in percent) for matching a tickmark position to a mark position. */
const MAJOR_TICK_THRESHOLD_PERCENT = 0.5;
/** Soft cap for decorative tickmarks to avoid rendering excessive DOM. */
export const MAX_DECORATIVE_TICKMARK_COUNT = 100;

/** A mark augmented with a stagger `level` for label collision avoidance. */
export interface MarkLevel {
  label?: ReactNode;
  /** The stagger row (0 = default row, 1+ = offset rows below). */
  level: number;
  value: number;
}

/** Metadata for a single tickmark dot rendered on the slider rail. */
export interface TickmarkMeta {
  /** Whether this tick aligns with a user-defined mark (rendered larger). */
  isMajor: boolean;
  /** Position of the tick as a percentage (0–100) along the track. */
  percent: number;
}

/** Computed CSS-ready metrics for the active track segment. */
export interface TrackMetrics {
  /** Length of the active track as a percentage of the full range. */
  leap: number;
  /** Start position of the active track as a percentage of the full range. */
  offset: number;
}

type InsetValue = number | string;

const formatInsetValue = (value: InsetValue): string => {
  if (typeof value !== 'number') {
    return value;
  }

  return value === 0 ? '0' : `${value}%`;
};

const createInset = (top: InsetValue, right: InsetValue, bottom: InsetValue, left: InsetValue): string => {
  return `inset(${formatInsetValue(top)} ${formatInsetValue(right)} ${formatInsetValue(bottom)} ${formatInsetValue(left)})`;
};

/** Default value label formatter — returns the numeric value as-is. */
export const defaultValueLabelFormat = (x: number): number => x;

const normalizeCustomMarks = (marks: ReadonlyArray<Mark>, min: number, max: number): Mark[] => {
  return marks
    .map((mark, index) => ({
      ...mark,
      index,
      value: Number.isFinite(mark.value) ? clamp(mark.value, min, max) : min,
    }))
    .toSorted((markA, markB) => asc(markA.value, markB.value) || asc(markA.index, markB.index))
    .map(({ index, ...mark }) => mark);
};

/**
 * Resolves the public `marks` prop into a concrete array of mark objects.
 *
 * - `marks={true}` generates marks from `min` to `max` using `step`.
 * - custom mark arrays are normalized into a stable ascending range order.
 * - when the generated step grid does not land exactly on `max`, the endpoint
 *   is appended so the full range remains visibly represented.
 */
export const buildMarks = (
  marksProp: boolean | ReadonlyArray<Mark>,
  min: number,
  max: number,
  step: null | number,
): Mark[] => {
  if (marksProp !== true) {
    return Array.isArray(marksProp) ? normalizeCustomMarks(marksProp, min, max) : [];
  }

  if (step === null) {
    return [];
  }

  const generatedMarks = Array.from({ length: Math.floor((max - min) / step) + 1 }, (_, index) => ({
    value: roundValueToStep(min + step * index, step, min),
  }));

  if (generatedMarks.at(-1)?.value !== max) {
    generatedMarks.push({ value: max });
  }

  return generatedMarks;
};

/**
 * Converts an absolute slider value to a percentage (0–100) within the [min, max] range.
 *
 * @param value - The absolute value.
 * @param min - The slider minimum.
 * @param max - The slider maximum.
 * @returns The percentage position, or 0 if range is zero/negative.
 */
export const toPercent = (value: number, min: number, max: number): number => {
  const range = max - min;
  if (range <= 0) return 0;
  return ((value - min) / range) * 100;
};

/**
 * Computes staggered label levels to avoid overlapping mark labels.
 * Returns marks augmented with a `level` property (0, 1, or 2).
 */
export const computeMarkLevels = <MarkEntry extends { label?: ReactNode; value: number }>(
  marksList: MarkEntry[],
  min: number,
  max: number,
): Array<MarkEntry & { level: number }> => {
  const labeled = marksList.filter((m) => m.label != null);
  if (labeled.length === 0) return [];

  const sorted = [...labeled].toSorted((a, b) => a.value - b.value);

  // Track the right edge of the last label placed on each stagger level.
  // Initialized to -Infinity so the first label on each level always fits.
  const levelEdges = Array.from({ length: LABEL_LEVEL_COUNT }, () => -Infinity);

  const getWidth = (label: ReactNode): number => {
    const text = typeof label === 'string' ? label : String(label);
    // Use spread to count Unicode code points, not UTF-16 code units (handles emoji/surrogate pairs)
    return Math.max([...text].length * LABEL_CHAR_WIDTH_PERCENT, LABEL_MIN_WIDTH_PERCENT);
  };

  return sorted.map((mark) => {
    const percent = toPercent(mark.value, min, max);
    const width = getWidth(mark.label);
    // Center the label around the mark position
    const left = percent - width / 2;

    // Try to place the label on the first level where it doesn't overlap
    for (let i = 0; i < levelEdges.length; i++) {
      if (left > levelEdges[i] + LABEL_GAP_PERCENT) {
        levelEdges[i] = left + width;
        return { ...mark, level: i };
      }
    }

    // All levels are full — force the label onto the last level
    const fallbackLevel = levelEdges.length - 1;
    levelEdges[fallbackLevel] = left + width;
    return { ...mark, level: fallbackLevel };
  });
};

/**
 * Snaps a value to the nearest mark if within the threshold.
 */
/**
 * Snaps a single value to the nearest mark if the distance is within `threshold`.
 * If no mark is close enough the original value is returned unchanged.
 *
 * @param rawValue - The raw (unsnapped) slider value.
 * @param marksArr - The array of available marks.
 * @param threshold - Maximum distance (in slider units) for a snap to occur.
 * @returns The snapped value, or `rawValue` if no mark is close enough.
 */
export const snapToNearestMark = (rawValue: number, marksArr: Mark[], threshold: number): number => {
  let closestValue = rawValue;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const mark of marksArr) {
    const distance = Math.abs(rawValue - mark.value);
    if (distance <= threshold && distance < closestDistance) {
      closestDistance = distance;
      closestValue = mark.value;
    }
  }

  return closestValue;
};

/**
 * Applies snap-to-mark behavior to a slider value. Works with both single values
 * and arrays (range sliders), snapping each value independently.
 *
 * @param value - The current slider value (number or number[]).
 * @param marksArr - The available marks.
 * @param threshold - Snap distance threshold in slider units.
 * @returns The snapped slider value.
 */
export const applySnapToValue = (value: SliderValue, marksArr: Mark[], threshold: number): SliderValue => {
  if (Array.isArray(value)) {
    return value.map((point) => snapToNearestMark(point, marksArr, threshold)) as SliderValue;
  }

  return snapToNearestMark(value, marksArr, threshold);
};

/*
 * Resolves the next value for keyboard-driven movement when snap-to-marks is enabled.
 *
 * The challenge: a single arrow-key step may not be large enough to escape the current
 * mark's snap zone. This function repeatedly advances the candidate value by `stepDelta`
 * until it snaps to a *different* mark (or reaches the slider boundary).
 *
 * - `currentValue` — the thumb's current value.
 * - `nextValue` — the value proposed by the keyboard step.
 * - `marks` — available marks.
 * - `min` / `max` — slider range.
 * - `snapThreshold` — snap distance in slider units.
 *
 * Returns the resolved next value that snaps to a different mark.
 */
export const resolveKeyboardSnapValue = ({
  currentValue,
  marks,
  max,
  min,
  nextValue,
  snapThreshold,
}: {
  currentValue: number;
  marks: Mark[];
  max: number;
  min: number;
  nextValue: number;
  snapThreshold: number;
}): number => {
  // Direction and magnitude of the keyboard step
  const stepDelta = nextValue - currentValue;

  if (marks.length === 0 || snapThreshold <= 0 || stepDelta === 0) {
    return clamp(nextValue, min, max);
  }

  // Determine which mark the thumb is currently snapped to
  const currentSnappedValue = snapToNearestMark(currentValue, marks, snapThreshold);
  let candidateValue = clamp(nextValue, min, max);
  let nextSnappedValue = snapToNearestMark(candidateValue, marks, snapThreshold);

  // Guard against infinite loops: the maximum possible steps across the full range
  const maxIterations = Math.ceil((max - min) / Math.abs(stepDelta)) + 1;

  // Keep advancing by stepDelta until we land in a different mark's snap zone
  for (let iteration = 0; nextSnappedValue === currentSnappedValue && iteration < maxIterations; iteration += 1) {
    const advancedCandidateValue = clamp(candidateValue + stepDelta, min, max);

    // Stop if we've hit the slider boundary and can't advance further
    if (advancedCandidateValue === candidateValue) {
      break;
    }

    candidateValue = advancedCandidateValue;
    nextSnappedValue = snapToNearestMark(candidateValue, marks, snapThreshold);
  }

  return candidateValue;
};

/**
 * Calculates the snap threshold in absolute slider units from the percentage-based
 * `snapToMarks` prop. When `snapToMarks` is `true`, uses the default 2%.
 * When it's a number, uses that as the percentage.
 *
 * @param snapEnabled - Whether snapping is active.
 * @param snapToMarks - The `snapToMarks` prop value (`true` or a custom percentage).
 * @param min - Slider minimum.
 * @param max - Slider maximum.
 * @returns The snap threshold in slider units (0 when snapping is disabled).
 */
export const getSnapThreshold = (
  snapEnabled: boolean,
  snapToMarks: SliderProps['snapToMarks'],
  min: number,
  max: number,
): number => {
  if (!snapEnabled) return 0;
  const range = max - min;
  if (range <= 0) return 0;
  const thresholdPercent = typeof snapToMarks === 'number' ? snapToMarks : DEFAULT_SNAP_THRESHOLD_PERCENT;
  return range * (thresholdPercent / 100);
};

/*
 * Normalizes a raw numeric input value to a valid slider position.
 * Applies step rounding, mark snapping, and clamping to ensure the value
 * is always a legal slider position.
 *
 * Used by the visible number input (`withNumberInput`) to coerce typed values.
 *
 * - `value` — the raw input value.
 * - `step` — the step increment (`null` means marks-only mode).
 * - `marks` — available marks.
 * - `min` / `max` — slider range.
 * - `snapToMarks` — whether mark snapping is active.
 * - `snapThreshold` — snap distance in slider units.
 *
 * Returns the normalized value.
 */
export const normalizeVisibleInputValue = ({
  marks,
  max,
  min,
  snapThreshold = 0,
  snapToMarks = false,
  step,
  value,
}: {
  marks: Mark[];
  max: number;
  min: number;
  snapThreshold?: number;
  snapToMarks?: boolean;
  step: null | number;
  value: number;
}): number => {
  let nextValue = value;

  if (step === null && marks.length > 0) {
    // Marks-only mode: snap to the closest mark position since there's no step grid
    const closestIndex = findClosest(
      marks.map((mark) => mark.value),
      nextValue,
    );
    nextValue = marks[closestIndex]?.value ?? min;
  } else if (step != null) {
    // Step mode: round to the nearest step increment
    nextValue = roundValueToStep(nextValue, step, min);
  }

  nextValue = clamp(nextValue, min, max);

  if (snapToMarks && marks.length > 0 && step !== null) {
    // Apply magnetic snap to nearby marks after step rounding
    nextValue = snapToNearestMark(nextValue, marks, snapThreshold);
  }

  return clamp(nextValue, min, max);
};

/**
 * Computes the CSS offset and length of the colored track segment.
 * Supports all track modes: `normal`, `inverted`, `center`, and `false`.
 *
 * - **normal**: track runs from `min` (or the lower thumb) to the value.
 * - **inverted**: the track colors the *remaining* portion instead.
 * - **center**: track runs from the midpoint of the range to the current value.
 * - **false**: no track rendered (returns zeros).
 *
 * @param values - The current thumb value(s).
 * @param range - Whether the slider is a range slider.
 * @param min - Slider minimum.
 * @param max - Slider maximum.
 * @param track - The track display mode.
 * @returns `{ offset, leap }` as percentages (0–100).
 */
export const getTrackMetrics = (
  values: number[],
  range: boolean,
  min: number,
  max: number,
  track: SliderProps['track'] = 'normal',
): TrackMetrics => {
  if (values.length === 0) return { leap: 0, offset: 0 };
  const rangeSize = max - min;
  if (rangeSize <= 0) return { leap: 0, offset: 0 };

  // Center track mode: the track extends from the midpoint to the thumb value.
  // This is only meaningful for single-thumb sliders. For range sliders, we fall
  // through to standard range behavior (track spans between min and max thumbs).
  if (track === 'center' && !range) {
    const origin = min + rangeSize / 2;
    const startValue = Math.min(origin, values[0]);
    const endValue = Math.max(origin, values[0]);

    return {
      leap: ((endValue - startValue) / rangeSize) * 100,
      offset: toPercent(startValue, min, max),
    };
  }

  const minValue = range ? Math.min(...values) : min;
  const maxValue = range ? Math.max(...values) : values[0];
  const offset = toPercent(minValue, min, max);
  const leap = ((maxValue - minValue) / rangeSize) * 100;

  return { leap, offset };
};

/**
 * Builds metadata for evenly-spaced tickmark dots along the slider rail.
 * Each tickmark is either a normal dot or a "major" dot (when it aligns with a
 * user-defined mark position within `MAJOR_TICK_THRESHOLD_PERCENT`).
 *
 * @param tickmarks - Total number of tickmarks to render.
 * @param marksArray - User-defined marks (used to identify major ticks).
 * @param min - Slider minimum.
 * @param max - Slider maximum.
 * @returns Array of `{ percent, isMajor }` for each tickmark.
 */
export const buildTickmarkMeta = (
  tickmarks: number,
  marksArray: Mark[] | undefined,
  min: number,
  max: number,
): TickmarkMeta[] => {
  const resolvedTickmarkCount = Math.trunc(Math.min(Math.max(tickmarks, 0), MAX_DECORATIVE_TICKMARK_COUNT));

  if (resolvedTickmarkCount <= 0) return [];

  if (process.env.NODE_ENV !== 'production' && tickmarks > MAX_DECORATIVE_TICKMARK_COUNT) {
    console.warn(
      `Slider: \`tickmarks\` is decorative and is capped at \`${MAX_DECORATIVE_TICKMARK_COUNT}\` to keep rendering lightweight.`,
    );
  }

  const denominator = Math.max(resolvedTickmarkCount - 1, 1);
  const majorIndexes = new Set<number>();

  if (marksArray && marksArray.length > 0) {
    for (const mark of marksArray) {
      const markPercent = toPercent(mark.value, min, max);
      const approxIndex = Math.round((markPercent / 100) * denominator);
      const safeIndex = Math.min(Math.max(approxIndex, 0), resolvedTickmarkCount - 1);
      const tickPercent = resolvedTickmarkCount === 1 ? 0 : (safeIndex / denominator) * 100;
      if (Math.abs(markPercent - tickPercent) < MAJOR_TICK_THRESHOLD_PERCENT) {
        majorIndexes.add(safeIndex);
      }
    }
  }

  return Array.from({ length: resolvedTickmarkCount }).map((_, index) => ({
    isMajor: majorIndexes.has(index),
    percent: resolvedTickmarkCount === 1 ? 0 : (index / denominator) * 100,
  }));
};

/**
 * Determines whether a mark at a given percentage is within the active track region.
 * Used to style marks differently when they fall inside vs. outside the colored track.
 *
 * @param percent - The mark's position as a percentage (0–100).
 * @param range - Whether the slider is a range slider.
 * @param track - The track display mode.
 * @param trackOffset - The start of the active track (percentage).
 * @param trackLeap - The length of the active track (percentage).
 * @returns `true` if the mark is within the active track region.
 */
export const isMarkActive = (
  percent: number,
  range: boolean,
  track: SliderProps['track'],
  trackOffset: number,
  trackLeap: number,
): boolean => {
  if (track === 'center') {
    return percent >= trackOffset && percent <= trackOffset + trackLeap;
  }

  if (track === 'normal') {
    return range ? percent >= trackOffset && percent <= trackOffset + trackLeap : percent <= trackOffset + trackLeap;
  }

  if (track === 'inverted') {
    return range ? percent < trackOffset || percent > trackOffset + trackLeap : percent >= trackOffset + trackLeap;
  }

  return false;
};

/**
 * Generates a CSS `clip-path: inset(...)` value that clips the "active" tickmark
 * layer to only show ticks within the colored track region. The active layer is
 * rendered on top of the inactive layer and uses a different color.
 *
 * @param orientation - The slider orientation, used to choose horizontal vs. vertical clipping.
 * @param track - The track display mode.
 * @param range - Whether the slider is a range slider.
 * @param trackOffset - The start of the active track (percentage).
 * @param trackLeap - The length of the active track (percentage).
 * @returns One or more CSS `inset(...)` strings for the `clip-path` property.
 */
export const getTickmarkClipPaths = (
  orientation: SliderProps['orientation'],
  track: SliderProps['track'],
  range: boolean,
  trackOffset: number,
  trackLeap: number,
): string[] => {
  const activeEndPercent = trackOffset + trackLeap;

  if (track === false) {
    return [];
  }

  if (orientation === 'vertical') {
    if (track === 'center') {
      return [createInset(100 - activeEndPercent, 0, trackOffset, 0)];
    }

    if (track === 'inverted') {
      return range
        ? [createInset(100 - trackOffset, 0, 0, 0), createInset(0, 0, activeEndPercent, 0)]
        : [createInset(0, 0, activeEndPercent, 0)];
    }

    return range
      ? [createInset(100 - activeEndPercent, 0, trackOffset, 0)]
      : [createInset(100 - activeEndPercent, 0, 0, 0)];
  }

  if (track === 'center') {
    return [createInset(0, 100 - activeEndPercent, 0, trackOffset)];
  }

  if (track === 'inverted') {
    return range
      ? [createInset(0, 100 - trackOffset, 0, 0), createInset(0, 0, 0, activeEndPercent)]
      : [createInset(0, 0, 0, activeEndPercent)];
  }

  return range
    ? [createInset(0, 100 - activeEndPercent, 0, trackOffset)]
    : [createInset(0, 100 - activeEndPercent, 0, 0)];
};

/**
 * Backwards-compatible helper returning the first clip-path segment.
 * Prefer `getTickmarkClipPaths()` for new code.
 */
export const getTickmarkClipPath = (
  track: SliderProps['track'],
  range: boolean,
  trackOffset: number,
  trackLeap: number,
): string => {
  return getTickmarkClipPaths('horizontal', track, range, trackOffset, trackLeap)[0] ?? createInset(0, 0, 0, 0);
};

/**
 * Returns inline styles for the slider root element. Applies `width` for horizontal
 * sliders and `height` for vertical sliders when a length is explicitly provided.
 *
 * @param length - The desired length along the main axis (number for px, or a CSS string).
 * @param orientation - The slider orientation.
 * @returns CSS properties object, or `undefined` if no styles are needed.
 */
export const getSliderStyle = (
  length: SliderProps['length'],
  orientation: SliderProps['orientation'],
): CSSProperties | undefined => {
  if (length == null) return undefined;
  const value = typeof length === 'number' ? `${length}px` : length;
  return orientation === 'vertical' ? { height: value } : { width: value };
};

/**
 * Returns a clamped CSS offset for a mark dot so that the full circle stays
 * within the rail bounds. Without clamping, marks at 0% and 100% would have
 * half the dot extending beyond the rail due to `translate(-50%)`.
 *
 * The position is clamped between `mark-size / 2` and `100% - mark-size / 2`
 * using the `--rbui-slider-mark-size` CSS custom property, so the dot never
 * overflows the rounded rail ends regardless of the component size variant.
 *
 * @param percent - The mark's position as a percentage (0–100) along the track.
 * @param axis - The resolved axis direction (`horizontal`, `horizontal-reverse`, or `vertical`).
 * @returns CSS properties with the clamped positional value for the appropriate axis.
 */
export const getClampedMarkOffset = (percent: number, axis: Axis): CSSProperties => {
  const half = 'calc(var(--rbui-slider-mark-size) / 2)';
  const clamped = `clamp(${half}, ${percent}%, calc(100% - ${half}))`;

  if (axis === 'vertical') {
    return { bottom: clamped };
  }

  if (axis === 'horizontal-reverse') {
    return { right: clamped };
  }

  return { left: clamped };
};
